import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { FieldTip } from '@/components/FieldTip.js';
import { useWorkspace } from '@/context/WorkspaceContext.js';
import {
  FormModal,
  ModalButtonPrimary,
  ModalButtonSecondary,
} from '@/components/ui/FormModal.js';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function GerirEmpresasModal({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const { empresas, setActiveEmpresaId, refreshEmpresas } = useWorkspace();
  const [name, setName] = useState('');
  const [segment, setSegment] = useState('');

  const create = useMutation({
    mutationFn: async (payload: { name: string; segment?: string | null }) =>
      (await api.post('/empresas', payload)).data as { id: string },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['empresas'] });
      refreshEmpresas();
      setActiveEmpresaId(data.id);
      setName('');
      setSegment('');
    },
  });

  function handleCreate() {
    if (!name.trim()) return;
    create.mutate({ name: name.trim(), segment: segment.trim() || null });
  }

  return (
    <FormModal
      open={open}
      title="Gerir empresas"
      subtitle="Workspace ativo e novas empresas."
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <ModalButtonSecondary onClick={() => onOpenChange(false)}>Cancelar</ModalButtonSecondary>
          <ModalButtonPrimary
            disabled={!name.trim() || create.isPending}
            onClick={handleCreate}
          >
            {create.isPending ? 'A adicionar…' : 'Adicionar empresa'}
          </ModalButtonPrimary>
        </>
      }
    >
      <ul className="space-y-2 text-sm">
        {empresas.length === 0 && (
          <li className="text-ha-subtle">Nenhuma empresa registada.</li>
        )}
        {empresas.map((e) => (
          <li
            key={e.id}
            className="flex items-center justify-between border-b border-ha-border py-2"
          >
            <span>{e.name}</span>
            <button
              type="button"
              className="text-xs font-bold uppercase text-accent underline"
              onClick={() => {
                setActiveEmpresaId(e.id);
                onOpenChange(false);
              }}
            >
              Ativar
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-6 space-y-3 border-t border-ha-border pt-4">
        <FieldTip label="Nova empresa" hint="Nome da empresa ou projeto.">
          <input
            className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
          />
        </FieldTip>
        <FieldTip label="Segmento (opcional)" hint="Setor ou nicho.">
          <input
            className="w-full border border-ha-border2 bg-ha-card2 px-3 py-2 text-sm"
            value={segment}
            onChange={(ev) => setSegment(ev.target.value)}
          />
        </FieldTip>
      </div>
    </FormModal>
  );
}
